import { OfflineDocumentGroup } from '@luzula/luzula-lib';
import type { BodensteckbriefEntry } from './listenverwaltung/listen.service';

const D = 'assets/SEA/SEA Teil D 02.2026';

/** Statische SEA-PDF-Dokumente (Teile A–D) fuer den Offline-Download. */
export const SEA_PDF_URLS: string[] = [
	// Hauptteile (Teil D wird ueber den Bodenformenkatalog-Reader +
	// die unten gelisteten Einzelkapitel abgedeckt - die Sammeldoppel
	// "SEA-D Bodenformen-Katalog_Stand 02.26.pdf",
	// "SEA-D Bodenformen-Katalog Einführung_02.26.pdf",
	// "Anhydromorphe Böden_02.26.pdf", "Halbgleye_02-26.pdf",
	// "Graugleye_03-26.pdf", "Humusgleye_02-26.pdf" wurden im Mai 2026
	// entfernt, weil sie 1:1 die Einzelkapitel doppelten und das Asset-
	// Volume um ~75 MB aufgeblaeht haben.)
	'assets/SEA/SEA Teil A 02-2025.pdf',
	'assets/SEA/SEA 95 Teil B, Druckvorlage_Stand_20_3_25.pdf',
	'assets/SEA/SEA Teil C Zuordnungskatalog 03-25.pdf',
	// Teil D Einführung (Einzelteile statt frueheres Sammeldoppel)
	`${D}/01SeaDTitel_Stand_02.26.pdf`,
	`${D}/02SeaDInhalt(S.3-4)_02.26.pdf`,
	`${D}/03SeaDVorsp_Stand_02.26_S5-30.pdf`,
	`${D}/04Datenverzeichnis für Bodenformen(S.31-39)_02-26.pdf`,
	`${D}/04SeaDFeinbodenfverz_(S.40-80)_02-26.pdf`,
	// Anhydromorphe Böden (Einzelkapitel)
	`${D}/05Anhydro1Sandböden_02.26.pdf`,
	`${D}/05Anhydro2Bändersandböden_02-26.pdf`,
	`${D}/05Anhydro3Lehmsandböden_02_26.pdf`,
	`${D}/05Anhydro4Staubsandböden_02-26.pdf`,
	`${D}/05Anhydro5Bänderstaubsandböden_02-26.pdf`,
	`${D}/05Anhydro6Grandböden_02-26.pdf`,
	`${D}/05Anhydro7Bändergrandböden_02-26.pdf`,
	`${D}/05Anhydro8Skelettböden_02-26.pdf`,
	`${D}/05Anhydro9Kalkböden_02-26.pdf`,
	`${D}/05Anhydro10Lehmböden_02-26.pdf`,
	`${D}/05Anhydro11Tonböden_02-26.pdf`,
	// Halbgley-Böden (Einzelkapitel)
	`${D}/06Halbgley1Sandböden_02-26.pdf`,
	`${D}/06Halbgley2Skelettböden_02-26.pdf`,
	`${D}/06Halbgley3Lehmböden_02-26.pdf`,
	`${D}/06Halbgley4DeckTieflehmböden_02-26.pdf`,
	`${D}/06Halbgley5Tonböden_02-26.pdf`,
	// Graugley-Böden (Einzelkapitel)
	`${D}/07Graugley1Sandböden_02-26.pdf`,
	`${D}/07Graugley2Kalkböden_02-26.pdf`,
	`${D}/07Graugley3Lehmböden(S374-392)_02-26.pdf`,
	`${D}/07Graugley4Tonböden_02-26.pdf`,
	// Humusgley-Böden (Einzelkapitel)
	`${D}/08Humusgley1Sandböden-02-26.pdf`,
	`${D}/08Humusgley2Skelettböden_02-26.pdf`,
	`${D}/08Humusgley3Kalkböden_02-26.pdf`,
	`${D}/08Humusgley4Lehmböden_02-26.pdf`,
	`${D}/08Humusgley5Tonböden_02-26.pdf`,
	// Weitere
	`${D}/09Moore(S461)_neu_01-25.pdf`,
	`${D}/10 Komplexstandorte_02-26.pdf`,
];

/**
 * Asset-URL einer SEA95-Originalseite (Scan). Muss identisch zu der URL sein,
 * die die SeaOriginalComponent baut, damit der vorab geladene Service-Worker-
 * Cache-Eintrag beim spaeteren Anzeigen offline auch getroffen wird.
 */
export function seaOriginalWebpUrl(pdf: string, page: number): string {
	return `assets/SEA/originals/${encodeURIComponent(pdf)}/page_${page}.webp`;
}

/**
 * Leitet aus den Bodensteckbrief-Eintraegen die Menge der Original-Scan-URLs ab
 * (dedupliziert). Nur Eintraege mit `pdf` und gueltiger Seitenzahl zaehlen -
 * genau die, die der Katalog-Reader auch als „Original" anzeigen kann. So wird
 * offline exakt das geladen, was sich spaeter aufrufen laesst, und die Liste
 * pflegt sich beim Import neuer Steckbriefe selbst.
 */
export function bodensteckbriefeToOriginalUrls(
	entries: BodensteckbriefEntry[] | null | undefined,
): string[] {
	const set = new Set<string>();
	for (const e of entries ?? []) {
		if (e?.pdf && e.page != null && Number.isFinite(e.page)) {
			set.add(seaOriginalWebpUrl(e.pdf, e.page as number));
		}
	}
	return [...set];
}

/**
 * Offline-Dokumentgruppe fuer bpweb: EINE Checkbox „SEA-Dokumente", die sowohl
 * die statischen SEA-PDFs als auch die Original-Scans der Steckbriefe laedt
 * (bewusst kein zweiter Haken - User-Wunsch). Die Original-URLs werden erst beim
 * Download aus dem (zur Laufzeit geladenen) Bodensteckbrief-Katalog abgeleitet,
 * darum `resolveUrls` statt statischer `urls`.
 *
 * @param loadSteckbriefe liefert den aktuellen Bodensteckbrief-Katalog (aus dem
 *   ListenService); wird pro Download einmal ausgewertet.
 */
export function makeSeaDocumentGroups(
	loadSteckbriefe: () => Promise<BodensteckbriefEntry[] | null>,
): OfflineDocumentGroup[] {
	return [
		{
			label: 'SEA-Dokumente',
			hint: 'SEA-PDFs und Original-Scans der Steckbriefe für Offline-Nutzung',
			resolveUrls: async () => [
				...SEA_PDF_URLS,
				...bodensteckbriefeToOriginalUrls(await loadSteckbriefe()),
			],
		},
	];
}
